// YARN
import {
  GridEditBooleanCell,
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { Dispatch, SetStateAction } from 'react';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { frFR } from '@mui/x-date-pickers/locales';
// INTERFACES
import { IStackedParcelsRequestsRef } from 'components/generics/Interface/Exploitation/Parcels/IStackedParcelsRequestsRef';
import { IFormErrorObjects, IFormErrors } from 'components/generics/Interface/Commons/IErrorForm';
//THEMES
import { Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { StyledTooltipError } from './CustomCultureDataGridUtils';
import { ICampaign } from 'components/generics/Interface/Exploitation/Parcels/ICampaign';
import { ICulture } from 'components/generics/Interface/ICulture';
import { IParcelsList } from 'components/generics/Interface/Api/Response/Exploitation/Parcels/IParcelsList';

export const CustomParcelCell = (
  params: GridRenderCellParams,
  stackedParcelsRequestsRef: React.MutableRefObject<IStackedParcelsRequestsRef[] | []>,
  forecastCampaign: ICampaign | null = null
) => {
  const { id, value, field } = params;
  let hasBeenUpdated = false;

  if (stackedParcelsRequestsRef?.current.length) {
    stackedParcelsRequestsRef?.current.forEach((request) => {
      if (request.id === id) {
        if (request[field as keyof IStackedParcelsRequestsRef]) {
          hasBeenUpdated = true;
        }
        if (
          field === 'previousCulture1' &&
          forecastCampaign !== null &&
          request.parcelCultures !== undefined &&
          request?.parcelCultures[
            `${forecastCampaign.startYear - 1}-${forecastCampaign.endYear - 1}`
          ]
        ) {
          hasBeenUpdated = true;
        }
      }
    });
  }

  return (
    <Box className={`cellBox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      <div>{value}</div>
    </Box>
  );
};

export const CustomConversionDateParcelCell = (
  params: GridRenderCellParams,
  stackedParcelsRequestsRef: React.MutableRefObject<IStackedParcelsRequestsRef[] | []>
) => {
  const { id, value, field } = params;
  let hasBeenUpdated = false;

  if (stackedParcelsRequestsRef?.current.length) {
    stackedParcelsRequestsRef?.current.forEach((request) => {
      if (request.id === id && field in request) {
        hasBeenUpdated = true;
      }
    });
  }

  let displayValue = '';
  if (field === 'conversionDate') {
    if (dayjs(value).isValid()) {
      displayValue = dayjs(value).format('DD-MM-YYYY');
    } else {
      displayValue = 'Aucune';
    }
  } else {
    displayValue = value;
  }

  return (
    <Box className={`cellBox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      <div>{displayValue}</div>
    </Box>
  );
};

export const CustomParcelSurfaceCell = (
  params: GridRenderCellParams,
  stackedParcelsRequestsRef: React.MutableRefObject<IStackedParcelsRequestsRef[] | []>
) => {
  const { row, value } = params;
  const { surface, totalSurface } = value;

  let formattedSurface = surface;

  if (totalSurface !== surface) {
    formattedSurface = `${surface} (Total: ${totalSurface.toFixed(2).replace(/[.,]00$/, '')})`;
  }
  let hasBeenUpdated = false;

  if (stackedParcelsRequestsRef?.current.length) {
    stackedParcelsRequestsRef?.current.forEach((request) => {
      if (request?.id === row.id && request?.surface) {
        hasBeenUpdated = true;
      }
    });
  }
  return surface !== totalSurface ? (
    <Box className={`cellBox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      {params.value.surface} (Total: {params.value.totalSurface.toFixed(2).replace(/[.,]00$/, '')})
    </Box>
  ) : (
    <Box className={`cellBox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      {formattedSurface}
    </Box>
  );
};

export const customIsIrrigableCell = (
  params: GridRenderCellParams,
  stackedParcelsRequestsRef: React.MutableRefObject<IStackedParcelsRequestsRef[] | []>
) => {
  const { id, value } = params;
  let hasBeenUpdated = false;

  if (stackedParcelsRequestsRef?.current.length) {
    stackedParcelsRequestsRef?.current.forEach((request) => {
      if (request?.id === id && 'isIrrigable' in request) {
        hasBeenUpdated = true;
      }
    });
  }
  return value ? (
    <div className={`forecast_checkbox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      <CheckIcon />
    </div>
  ) : (
    <div className={`forecast_checkbox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      <CloseIcon />
    </div>
  );
};
export const customIsOrganicCell = (
  params: GridRenderCellParams,
  stackedParcelsRequestsRef: React.MutableRefObject<IStackedParcelsRequestsRef[] | []>
) => {
  const { id, value } = params;
  let hasBeenUpdated = false;

  if (stackedParcelsRequestsRef?.current.length) {
    stackedParcelsRequestsRef?.current.forEach((request) => {
      if (request?.id === id && 'organicState' in request) {
        hasBeenUpdated = true;
      }
    });
  }
  return value ? (
    <div className={`forecast_checkbox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      <CheckIcon />
    </div>
  ) : (
    <div className={`forecast_checkbox ${hasBeenUpdated ? 'cellHasBeenUpdated' : ''}`}>
      <CloseIcon />
    </div>
  );
};

type CustomEditTransitionDateCellParams = {
  params: GridRenderEditCellParams<any, Dayjs | null, string>;
  maxDate: Dayjs;
  errorsFormDefault: IFormErrors;
  isOpen: boolean;
  format: string;
};

export const CustomEditConversionDateCell = ({
  params,
  maxDate,
  errorsFormDefault,
  isOpen,
  format,
}: CustomEditTransitionDateCellParams) => {
  const { id, field, value } = params;
  let dateValue = dayjs(value);
  // Format year to get a full date for the date picker
  if (format === 'YYYY') {
    dateValue = dayjs(dayjs(value).format(`01-01-${value}`));
  }
  const apiRef = useGridApiContext();
  const handleChange = (newValue: Dayjs | null) => {
    apiRef.current.setEditCellValue({
      id,
      field,
      value: newValue ? newValue?.format(format) : null,
    });
  };

  const datePicker = () => (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="fr"
      localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <DatePicker
        value={dateValue}
        maxDate={format !== 'YYYY' ? maxDate : undefined}
        onChange={handleChange}
        format={format}
        closeOnSelect
        views={format === 'YYYY' ? ['year'] : ['year', 'month', 'day']}
        slotProps={{
          field: { clearable: true },
          clearButton: {
            className: 'custom-clear-button',
          },
          yearButton: {
            sx: {
              padding: '0',
            },
          },
        }}
      />
    </LocalizationProvider>
  );

  return errorsFormDefault.formError &&
    errorsFormDefault.hasOwnProperty(params.field) &&
    (errorsFormDefault[params.field as keyof IFormErrors] as IFormErrorObjects).message.length >
      0 ? (
    <>
      <StyledTooltipError
        open={isOpen}
        title={(errorsFormDefault[params.field as keyof IFormErrors] as IFormErrorObjects).message}
        placement="top"
      >
        <span>{datePicker()}</span>
      </StyledTooltipError>
    </>
  ) : (
    <span>{datePicker()}</span>
  );
};

type CustomEditOrganicStateCellParams = {
  params: GridRenderEditCellParams;
  setOpenOrganicStateDialog: Dispatch<SetStateAction<boolean>>;
  setModalProps: Dispatch<
    SetStateAction<{ resolve: (value: boolean) => void; reject: () => void }>
  >;
};

export const CustomEditOrganicStateCell = ({
  params,
  setOpenOrganicStateDialog,
  setModalProps,
}: CustomEditOrganicStateCellParams): React.JSX.Element => {
  const apiRef = useGridApiContext();
  const { id, field, value } = params;
  const handlechange = async (event: React.FormEvent<HTMLLabelElement>) => {
    const target = (event as React.SyntheticEvent).target as HTMLInputElement;
    if (target?.checked === false) {
      event.preventDefault();
      const confirmedChoice = await new Promise<boolean>((resolve, reject) => {
        setModalProps({
          resolve: () => {
            resolve(true);
            setOpenOrganicStateDialog(false);
          },
          reject: () => {
            resolve(false);
            setOpenOrganicStateDialog(false);
          },
        });
        setOpenOrganicStateDialog(true);
      });
      if (confirmedChoice) {
        apiRef.current.setEditCellValue({
          id,
          field,
          value: false,
        });
      } else {
        apiRef.current.setEditCellValue({
          id,
          field,
          value: true,
        });
      }
      apiRef.current.setCellFocus(id, field);
    } else {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: true,
      });
    }
  };
  return (
    <GridEditBooleanCell
      {...params}
      sx={{ borderBottom: '1px solid #e69636' }}
      onChange={handlechange}
    />
  );
};

export const getCultureCampaignKeyFromYear = (indexYear: number) => {
  return indexYear - 1 + '-' + indexYear;
};

export const getCultureCampaignKey = (indexYear: number, forecastCampaign: ICampaign) => {
  return forecastCampaign?.startYear - indexYear + '-' + (forecastCampaign?.endYear - indexYear);
};

export const getCultureCampaignName = (
  culture: IParcelsList['parcelCultures'],
  indexYear: number,
  forecastCampaign: ICampaign,
  type?: string
): string => {
  let defaultMessage = 'Pas de culture associée';
  if (type && type === 'forecast') {
    defaultMessage = 'Aucun forecast';
  }
  const campaignKey = getCultureCampaignKey(indexYear, forecastCampaign);
  return culture[campaignKey]?.name ?? defaultMessage;
};

export const filterParcelFromActivityDate = (
  parcels: IParcelsList[],
  forecastCampaign: ICampaign
): IParcelsList[] => {
  return parcels?.filter((parcel) => {
    const checkActivityEnd =
      !parcel.activityEnd ||
      (parcel.activityEnd && !(parcel.activityEnd < forecastCampaign.endYear));
    const checkActivityStart =
      !parcel.activityStart ||
      (parcel.activityStart && !(parcel.activityStart > forecastCampaign.startYear));
    if (checkActivityEnd && checkActivityStart) {
      return parcel;
    }
  });
};
